import { useMemo } from 'react';

import {
  useFetchProductReviews,
  useFetchProductReviewsAggregate,
} from '@jane/dm/internal';
import type { ReviewSort } from '@jane/dm/internal';
import type { Feeling } from '@jane/search/data-access';

const KIOSK_REVIEWS_PAGE_SIZE = 3;

export type InternalFeeling = {
  count: number;
  feeling: Feeling;
};

const toInternalFeelings = (
  distribution: Record<string, number> = {}
): InternalFeeling[] =>
  Object.entries(distribution)
    .map<InternalFeeling>(([feeling, count]) => ({
      count,
      feeling: feeling.toLowerCase() as Feeling,
    }))
    .filter(({ count }) => count > 0)
    .sort((a, b) => b.count - a.count);

export const useProductFeelings = (
  productId: number,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { data, isPending } = useFetchProductReviewsAggregate(productId, {
    enabled,
  });
  const feelings = useMemo(
    () => toInternalFeelings(data?.feelingsDistribution),
    [data?.feelingsDistribution]
  );
  return { data: feelings, isLoading: isPending };
};

interface UseProductReviewsOptions {
  enabled?: boolean;
  sort?: ReviewSort;
}

export const useProductReviews = (
  productId: number,
  { enabled = true, sort = 'recent' }: UseProductReviewsOptions = {}
) =>
  useFetchProductReviews({
    activeActivities: [],
    activeFeelings: [],
    activeRatings: [],
    enabled,
    // Kiosk PDP historically showed only reviews with a written comment.
    // Restore that default-on filter for the review LIST (the aggregate path
    // in useProductFeelings stays unfiltered so facets cover all reviews).
    hasComment: true,
    pageSize: KIOSK_REVIEWS_PAGE_SIZE,
    productId,
    sort,
  });
